import { TradeFlowCheckerStore } from '@ui/features/Trade/stores/TradeFlowCheckerStore';
import { useClosingTradesStore } from '@ui/features/Websocket/useClosingTradesStore';
import type { ClosingTrade } from '@ui/features/Websocket/useClosingTradesStore';
import { usePendingTradesStore } from '@ui/features/Websocket/usePendingTradesStore';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { useGetPositionV3Batch } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV3Batch/useGetPositionV3Batch';
import { useMemo } from 'react';
import type { Address } from 'viem';

import {
  getPositionRefreshInterval,
  usePositionRefreshAccelerationStore,
} from './usePositionRefreshAcceleration';

const isPositionClosing = (
  position: { id: string; timestamp?: number },
  closingTrade: ClosingTrade,
) => {
  if (position.id !== closingTrade.positionHash) return false;

  if (closingTrade.positionTimestamp !== undefined) {
    return position.timestamp === closingTrade.positionTimestamp;
  }

  if (!position.timestamp) return true;

  const positionTimestampMs =
    position.timestamp > 1_000_000_000_000
      ? position.timestamp
      : position.timestamp * 1000;

  return positionTimestampMs <= closingTrade.submittedAt;
};

export const usePositions = (userAddress?: Address) => {
  const { data: pairs } = useBackendPairsAll();
  const pendingTrades = usePendingTradesStore((s) => s.usePendingTrades());
  const closingTrades = useClosingTradesStore((s) => s.useClosingTrades());
  const tradeFlowByKey = TradeFlowCheckerStore((s) => s.byKey);
  const isFastestRefreshActive = usePositionRefreshAccelerationStore(
    (s) => !userAddress && s.fastestRefreshUntil > Date.now(),
  );
  const hasRecentSubmittedTrade =
    !userAddress &&
    Object.values(tradeFlowByKey).some(
      ({ marketTradeReceiptSent }) =>
        Date.now() - new Date(marketTradeReceiptSent).getTime() < 30_000,
    );
  const hasPendingPositionState =
    !userAddress &&
    (pendingTrades.length > 0 ||
      closingTrades.length > 0 ||
      hasRecentSubmittedTrade);

  const { data: positionsQueryData, isPending } = useGetPositionV3Batch(
    useMemo(
      () => ({
        pairs: pairs,
        interval: getPositionRefreshInterval({
          isFastestRefreshActive,
          hasPendingPositionState,
        }),
        userAddress,
      }),
      [
        hasPendingPositionState,
        isFastestRefreshActive,
        pairs,
        userAddress,
      ],
    ),
  );

  // Combine confirmed positions with pending trades
  const combinedPositions = useMemo(() => {
    if (!positionsQueryData) {
      return userAddress ? [] : pendingTrades;
    }

    // Create a set of position IDs from the query data
    const confirmedPositionIds = new Set(
      positionsQueryData.map((position) => position.id),
    );

    // Filter out pending trades that have the same ID as positions in positionsQuery.data
    const filteredPendingTrades = pendingTrades.filter(
      (pendingTrade) => !confirmedPositionIds.has(pendingTrade.id),
    );

    // Filter only the position lifecycle that submitted the close.
    const filteredPositionsQueryData = positionsQueryData.filter(
      (position) =>
        userAddress ||
        !closingTrades.some((closingTrade) =>
          isPositionClosing(position, closingTrade),
        ),
    );

    // Combine filtered pending trades with filtered positionsQuery.data and sort by timestamp (newest first)
    return [...filteredPendingTrades, ...filteredPositionsQueryData].sort(
      (a, b) => (b.timestamp || 0) - (a.timestamp || 0),
    );
  }, [positionsQueryData, pendingTrades, closingTrades, userAddress]);

  return useMemo(
    () => ({
      data: combinedPositions,
      isPending,
    }),
    [combinedPositions, isPending],
  );
};
